// client/src/components/MerchantPubViewer.jsx

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MerchantNavbar from './MerchantNavbar';
import { FaArrowLeft } from 'react-icons/fa';
import { ref, getMetadata, updateMetadata, deleteObject } from 'firebase/storage';
import { storage } from '../../firebase';

// ---- utils ----
const urlToStoragePath = (url = '') => {
  // Exemple: .../o/UID%2Fpub%2Ffile.pdf?alt=media&token=...
  const m = url.match(/\/o\/(.*?)\?/);
  if (!m || !m[1]) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return null;
  }
};

const kindFromContentType = (ct = '') => {
  if (!ct) return null;
  if (ct.startsWith('image/')) return 'image';
  if (ct === 'application/pdf') return 'pdf';
  if (ct.startsWith('video/')) return 'video';
  return 'other';
};

const kindFromUrlExt = (url = '') => {
  const lower = url.toLowerCase();
  if (lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image';
  if (lower.endsWith('.pdf')) return 'pdf';
  if (lower.endsWith('.mp4')) return 'video';
  return 'other';
};

const MerchantPubViewer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const urlParams = new URLSearchParams(location.search);
  const fileUrl = urlParams.get('url') || '';

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [originalStart, setOriginalStart] = useState('');
  const [originalEnd, setOriginalEnd] = useState('');
  const [kind, setKind] = useState('other');
  const [metaLoaded, setMetaLoaded] = useState(false);

  // Charger métadonnées (dates + type)
  useEffect(() => {
    if (!fileUrl) return;

    const path = urlToStoragePath(fileUrl);
    if (!path) {
      // Pas de chemin : on fait au mieux avec l’extension pour l’aperçu
      setKind(kindFromUrlExt(fileUrl));
      setMetaLoaded(true);
      return;
    }

    const fileRef = ref(storage, path);
    getMetadata(fileRef)
      .then((metadata) => {
        const mStart = (metadata.customMetadata && metadata.customMetadata.start) ? metadata.customMetadata.start : '';
        const mEnd = (metadata.customMetadata && metadata.customMetadata.end) ? metadata.customMetadata.end : '';

        setStartDate(mStart);
        setEndDate(mEnd);
        setOriginalStart(mStart);
        setOriginalEnd(mEnd);

        const detected = kindFromContentType(metadata.contentType);
        setKind(detected ? detected : kindFromUrlExt(fileUrl));
      })
      .catch((err) => {
        console.error('getMetadata error:', err);
        // Fallback sur extension pour la preview si erreur
        setKind(kindFromUrlExt(fileUrl));
      })
      .finally(() => setMetaLoaded(true));
  }, [fileUrl]);

  const handleBack = () => navigate('/merchantgestionpromo');

  const handleSaveDates = () => {
    if (!fileUrl) return;
    const path = urlToStoragePath(fileUrl);
    if (!path) return;

    const fileRef = ref(storage, path);
    const newMetadata = {
      customMetadata: {
        start: startDate || '',
        end: endDate || '',
      },
    };

    updateMetadata(fileRef, newMetadata)
      .then(() => {
        alert('Dates mises à jour avec succès !');
        setOriginalStart(startDate);
        setOriginalEnd(endDate);
      })
      .catch((error) => {
        console.error('updateMetadata error:', error);
        alert("Erreur lors de la mise à jour des dates.");
      });
  };

  const handleDelete = () => {
    if (!fileUrl) return;
    const path = urlToStoragePath(fileUrl);
    if (!path) return;

    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette publication ?')) {
      const fileRef = ref(storage, path);
      deleteObject(fileRef)
        .then(() => navigate(-1))
        .catch((error) => {
          console.error('deleteObject error:', error);
          alert('Erreur lors de la suppression.');
        });
    }
  };

  const renderPreview = () => {
    if (!fileUrl) return <p className="eb-muted" style={{ margin: 0, padding: 32, textAlign: 'center' }}>Aucun contenu à afficher</p>;
    if (!metaLoaded) return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <span className="eb-spinner" role="status" aria-label="Chargement" />
      </div>
    );

    const wrapper = {
      margin: 0,
      textAlign: 'center',
    };
    const frame = {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: 'min(70vh, 720px)',
      margin: 'auto',
      overflow: 'hidden',
      borderRadius: 'var(--eb-radius)',
      background: 'var(--eb-surface-2)',
    };

    if (kind === 'image') {
      return (
        <div style={wrapper}>
          <div style={frame}>
            <img
              src={fileUrl}
              alt="Prévisualisation"
              style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain', display: 'block' }}
            />
          </div>
        </div>
      );
    }

    if (kind === 'video') {
      return (
        <div style={wrapper}>
          <div style={frame}>
            <video
              src={fileUrl}
              controls
              playsInline
              preload="metadata"
              style={{ maxWidth: '100%', maxHeight: '100%', width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          </div>
        </div>
      );
    }

    if (kind === 'pdf') {
      // L'URL renvoyée par getDownloadURL sert le bon Content-Type, embeddable.
      const pdfUrl = `${fileUrl}#zoom=page-fit&scrollbar=1&toolbar=1&navpanes=0`;
      return (
        <div style={wrapper}>
          <div style={frame}>
            <iframe
              title="Prévisualisation PDF"
              src={pdfUrl}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          </div>
        </div>
      );
    }

    return (
      <div style={wrapper}>
        <div style={frame}>
          <p className="eb-muted" style={{ padding: 8, margin: 0 }}>Aperçu non disponible pour ce type de fichier.</p>
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard-container">
      <MerchantNavbar />

      <style>{`
        .mpv-scope { width: 100%; max-width: 960px; min-width: 0; }
        .mpv-scope .eb-page-title { margin: 0; text-align: left; }
        .mpv-scope .mpv-back { margin-bottom: 12px; padding-left: 8px; }
        .mpv-scope .mpv-card { background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); box-shadow: var(--eb-shadow-sm); padding: 20px; margin-bottom: 16px; }
        .mpv-scope .mpv-dates { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; }
        .mpv-scope .mpv-actions { display: flex; justify-content: center; gap: 10px; flex-wrap: wrap; margin-top: 16px; }
        @media (max-width: 768px) {
          .mpv-scope .mpv-card { padding: 16px; }
          .mpv-scope .mpv-actions > button { width: 100%; }
        }
      `}</style>

      <div className="mpv-scope">
        <button onClick={handleBack} className="eb-btn eb-btn--ghost eb-btn--sm mpv-back">
          <FaArrowLeft /> Retour
        </button>
        <header className="eb-page-header">
          <div>
            <h1 className="eb-page-title">Promotion</h1>
            <p className="eb-page-subtitle">Période de diffusion et aperçu du contenu</p>
          </div>
          <button onClick={handleDelete} className="eb-btn eb-btn--secondary" style={{ color: 'var(--eb-danger)' }}>
            Supprimer
          </button>
        </header>

        {/* Dates */}
        <div className="mpv-card">
          <div className="mpv-dates">
            <div className="eb-field">
              <label htmlFor="startDate" className="eb-label">Date de début</label>
              <input
                id="startDate"
                type="datetime-local"
                className="eb-input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="eb-field">
              <label htmlFor="endDate" className="eb-label">Date de fin</label>
              <input
                id="endDate"
                type="datetime-local"
                className="eb-input"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
          {(startDate !== originalStart || endDate !== originalEnd) && (
            <div className="mpv-actions">
              <button onClick={handleSaveDates} className="eb-btn eb-btn--primary">
                Enregistrer modifications
              </button>
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="mpv-card" style={{ padding: 12 }}>
          {renderPreview()}
        </div>
      </div>
    </div>
  );
};

export default MerchantPubViewer;
